import React, { useRef, useState } from 'react';
import {
    Play, Download, FileText, Share2, Sparkles, GraduationCap,
    Gamepad2, Trophy, Globe2, LayoutDashboard, BadgeCheck,
} from 'lucide-react';

const ASSET = (p) => `/movie-assets/${p}`;

const FEATURES = [
    { icon: Sparkles, title: 'Social learning feed', desc: 'A familiar feed of bite-sized courses, likes, comments and disappearing stories.' },
    { icon: GraduationCap, title: 'Structured courses', desc: 'Video lessons, knowledge checkpoints and a no-skip player — in ten Indian languages.' },
    { icon: Gamepad2, title: 'The Arcade', desc: 'Branded games like Pulse Power and Pulse Crush make building skills addictive.' },
    { icon: Trophy, title: 'Badges & leaderboards', desc: 'Points, milestone badges and live state, regional & national rankings.' },
    { icon: Globe2, title: 'Rewards that pay off', desc: 'Loyalty integration and single sign-on turn knowledge into real rewards.' },
    { icon: LayoutDashboard, title: 'Admin command center', desc: 'Live KPIs, multi-dimensional reports and effortless content publishing.' },
];

export default function MoviePage() {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const play = () => {
        const v = videoRef.current;
        if (!v) return;
        v.play();
        setPlaying(true);
    };

    const share = async () => {
        const data = {
            title: 'Learning Hub',
            text: 'Learning Hub — an enterprise learning, rewards & gamified training platform. Learn. Play. Grow.',
            url: window.location.href,
        };
        try {
            if (navigator.share) await navigator.share(data);
            else {
                await navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard');
            }
        } catch (e) { /* cancelled */ }
    };

    return (
        <div
            data-testid="movie-page"
            className="min-h-screen text-emerald-50 font-sans"
            style={{ background: 'radial-gradient(120% 90% at 78% 8%, #0a5a3a 0%, #043a26 32%, #021a12 64%, #00100b 100%)' }}
        >
            {/* Nav */}
            <header className="flex items-center justify-between px-6 sm:px-10 py-5 max-w-7xl mx-auto">
                <div className="flex items-center gap-3" data-testid="movie-brand">
                    <img src={ASSET('logo.png')} alt="Learning Hub" className="w-11 h-11 rounded-xl object-contain"
                        style={{ boxShadow: '0 8px 26px rgba(0,219,121,.35)' }} />
                    <span className="text-2xl font-extrabold tracking-tight">Learning <span className="text-[#00db79]">Hub</span></span>
                </div>
                <button
                    onClick={share}
                    data-testid="movie-share-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[#00db79]/40 bg-[#00db79]/10 px-4 py-2 text-sm font-semibold text-[#9df5ce] hover:bg-[#00db79]/20 transition-colors"
                >
                    <Share2 className="w-4 h-4" /> Share
                </button>
            </header>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 sm:px-10 pt-6 sm:pt-10 text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#00db79]/10 border border-[#00db79]/30 px-4 py-1.5 text-xs font-bold tracking-widest text-[#00db79] uppercase" data-testid="movie-eyebrow">
                    <Sparkles className="w-3.5 h-3.5" /> Product Walkthrough
                </div>
                <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
                    Learn. Play. <span className="text-[#00db79]">Grow.</span>
                </h1>
                <p className="mt-5 text-base sm:text-lg text-emerald-100/80 max-w-2xl mx-auto">
                    An enterprise learning, rewards and gamified training platform that turns everyday
                    training into a game your teams actually want to play.
                </p>
            </section>

            {/* Video */}
            <section className="max-w-5xl mx-auto px-4 sm:px-10 mt-10">
                <div
                    className="relative rounded-3xl overflow-hidden border border-[#00db79]/25 bg-black/40"
                    style={{ boxShadow: '0 40px 120px rgba(0,0,0,.55), 0 0 0 1px rgba(0,219,121,.12)' }}
                    data-testid="movie-player-wrap"
                >
                    <video
                        ref={videoRef}
                        data-testid="movie-video"
                        className="w-full block bg-black aspect-video"
                        poster={ASSET('poster.jpg')}
                        controls={playing}
                        playsInline
                        preload="metadata"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                    >
                        <source src={ASSET('movie.mp4')} type="video/mp4" />
                    </video>
                    {!playing && (
                        <button
                            onClick={play}
                            data-testid="movie-play-btn"
                            className="absolute inset-0 flex items-center justify-center group"
                            aria-label="Play the Learning Hub movie"
                        >
                            <span className="flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#00db79] text-[#00160e] shadow-2xl transition-transform group-hover:scale-110"
                                style={{ boxShadow: '0 0 60px rgba(0,219,121,.6)' }}>
                                <Play className="w-9 h-9 sm:w-11 sm:h-11 ml-1" fill="currentColor" />
                            </span>
                        </button>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="movie-actions">
                    <a
                        href={ASSET('movie.mp4')}
                        download="Learning-Hub-Movie.mp4"
                        data-testid="movie-download-mp4"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#00db79] px-7 py-3.5 text-sm font-bold text-[#00160e] hover:bg-[#25e88f] transition-colors"
                    >
                        <Download className="w-4 h-4" /> Download the movie (MP4)
                    </a>
                    <a
                        href={ASSET('Learning-Hub-Brochure.pdf')}
                        download="Learning-Hub-Brochure.pdf"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="movie-download-pdf"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-[#00db79]/40 bg-transparent px-7 py-3.5 text-sm font-bold text-[#9df5ce] hover:bg-[#00db79]/10 transition-colors"
                    >
                        <FileText className="w-4 h-4" /> Download the brochure (PDF)
                    </a>
                </div>
            </section>

            {/* Features */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 mt-20">
                <h2 className="text-base md:text-lg font-bold tracking-widest text-[#00db79] uppercase text-center">Inside Learning Hub</h2>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="movie-features">
                    {FEATURES.map((f, i) => (
                        <div key={i} data-testid={`movie-feature-${i}`}
                            className="rounded-2xl border border-[#00db79]/15 bg-[#0a2418]/60 backdrop-blur-sm p-6 hover:border-[#00db79]/40 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-[#00db79]/15 flex items-center justify-center text-[#00db79]">
                                <f.icon className="w-6 h-6" />
                            </div>
                            <h3 className="mt-4 text-lg font-bold text-emerald-50">{f.title}</h3>
                            <p className="mt-2 text-sm text-emerald-100/70 leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-4xl mx-auto px-6 sm:px-10 mt-20 mb-16 text-center">
                <div className="rounded-3xl border border-[#00db79]/25 bg-[#00db79]/10 p-10">
                    <BadgeCheck className="w-10 h-10 text-[#00db79] mx-auto" />
                    <h2 className="mt-4 text-2xl sm:text-3xl font-black">Ready to transform how your teams learn?</h2>
                    <p className="mt-3 text-emerald-100/75">Learning that people love. Skills that actually stick. Results you can see.</p>
                    <button
                        onClick={share}
                        data-testid="movie-cta-share-btn"
                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00db79] px-7 py-3.5 text-sm font-bold text-[#00160e] hover:bg-[#25e88f] transition-colors"
                    >
                        <Share2 className="w-4 h-4" /> Share Learning Hub
                    </button>
                </div>
            </section>

            <footer className="text-center pb-10 text-xs text-emerald-100/40">
                Learning Hub &middot; Learn. Play. Grow.
            </footer>
        </div>
    );
}
